import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    Grid, GridColumn, Container, Header, Button, Loader, Dimmer,
    FormInput, Form, Message
} from 'semantic-ui-react';
import { useQuery } from '@tanstack/react-query';
import { getConfiguration } from './lib/configuration';
import { isLoggedIn, login } from './lib/authentication';
import { useAuthStore } from './lib/authStore';
import { buildPath } from './lib/tenant';
import { t } from './locales';

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginFailed, setLoginFailed] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const next = new URLSearchParams(location.search).get('next') || buildPath('/events');
    const queryParams = location.search;

    const configQuery = useQuery({
        queryKey: ['configuration'],
        queryFn: getConfiguration,
        staleTime: 60 * 60 * 1000,
    });

    useEffect(() => {
        isLoggedIn().then(loggedIn => {
            if (loggedIn) {
                navigate(next + (queryParams && !queryParams.startsWith('?next') ? '' : ''), { replace: true });
            }
            setPageLoading(false);
        });
    }, [navigate, next, queryParams]);

    const processLogin = async (event) => {
        event?.preventDefault();
        setLoginFailed(false);
        const result = await login({ username, password });
        if (result) {
            await useAuthStore.getState().login(result.token);
            navigate(next, { replace: true });
        } else {
            setLoginFailed(true);
        }
    };

    if (pageLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('login.loading')} />
            </Dimmer>
        );
    }

    const logo = configQuery.data?.logo;

    return (
        <Grid className="darkLabel" stackable columns style={{ padding: '10%' }}>
            <GridColumn color="white" width={3} />
            <GridColumn color="white" width={10}>
                <div className="authCard">
                    {logo && (
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <img src={logo} alt="Logo" style={{ maxHeight: 60, objectFit: 'contain' }} />
                        </div>
                    )}
                    <Container textAlign="center">
                        <Header className="authHeader">{t('login.title')}</Header>

                        {loginFailed && (
                            <Message negative style={{ textAlign: 'left' }}>
                                {t('login.incorrectCredentials')}
                            </Message>
                        )}

                        <Form onSubmit={processLogin}>
                            <FormInput
                                label={t('login.usernameLabel')}
                                value={username}
                                onChange={(_, { value }) => setUsername(value)}
                            />
                            <FormInput
                                label={t('login.passwordLabel')}
                                type="password"
                                value={password}
                                onChange={(_, { value }) => setPassword(value)}
                            />
                            <br />
                            <Button type="submit" fluid className="buttonx">
                                {t('login.submitButton')}
                            </Button>
                        </Form>

                        <p className="options" style={{ marginTop: '1.5rem' }}>
                            {t('login.noAccount')}
                            <Link to={buildPath('/register') + location.search}> {t('login.createOne')}</Link>
                        </p>
                    </Container>
                </div>
            </GridColumn>
            <GridColumn color="white" width={3} />
        </Grid>
    );
}

export default Login;
